import { useEffect, useRef, useState, type RefObject } from 'react';
import './CropOverlay.css';

export interface CropRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

type Handle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'move';

interface Props {
  canvasRef: RefObject<HTMLCanvasElement>;
  rect: CropRect;
  // Normalised aspect (width/height in 0..1 crop space). null = free-form.
  aspect: number | null;
  onChange: (r: CropRect) => void;
}

const MIN = 0.05;

const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v));

// Aspect-locked corner resize: keep the opposite corner fixed and derive the
// height from the width so the ratio is preserved, clamped to the image bounds.
function cornerAspect(
  handle: Handle,
  start: CropRect,
  px: number,
  aspect: number,
): CropRect {
  const goLeft = handle.includes('w');
  const goUp = handle.includes('n');
  const anchorX = goLeft ? start.x + start.width : start.x;
  const anchorY = goUp ? start.y + start.height : start.y;

  const maxWx = goLeft ? anchorX : 1 - anchorX;
  const maxHy = goUp ? anchorY : 1 - anchorY;
  const minW = Math.max(MIN, MIN * aspect);

  let w = Math.abs(px - anchorX);
  w = Math.min(Math.max(w, minW), maxWx, maxHy * aspect);
  const h = w / aspect;

  const x = goLeft ? anchorX - w : anchorX;
  const y = goUp ? anchorY - h : anchorY;
  return { x, y, width: w, height: h };
}

function computeRect(
  handle: Handle,
  start: CropRect,
  px: number,
  py: number,
  startPx: number,
  startPy: number,
  aspect: number | null,
): CropRect {
  if (handle === 'move') {
    const dx = clamp(px - startPx, -start.x, 1 - (start.x + start.width));
    const dy = clamp(py - startPy, -start.y, 1 - (start.y + start.height));
    return { x: start.x + dx, y: start.y + dy, width: start.width, height: start.height };
  }

  if (aspect != null) {
    return cornerAspect(handle, start, px, aspect);
  }

  let x0 = start.x;
  let y0 = start.y;
  let x1 = start.x + start.width;
  let y1 = start.y + start.height;
  if (handle.includes('n')) y0 = clamp(py, 0, y1 - MIN);
  if (handle.includes('s')) y1 = clamp(py, y0 + MIN, 1);
  if (handle.includes('w')) x0 = clamp(px, 0, x1 - MIN);
  if (handle.includes('e')) x1 = clamp(px, x0 + MIN, 1);
  return { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
}

export function CropOverlay({ canvasRef, rect, aspect, onChange }: Props) {
  const [box, setBox] = useState<DOMRect | null>(null);
  const drag = useRef<{
    handle: Handle;
    start: CropRect;
    startPx: number;
    startPy: number;
  } | null>(null);

  // Track the canvas position/size so the overlay always aligns with the image.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const update = () => setBox(canvas.getBoundingClientRect());
    update();
    const ro = new ResizeObserver(update);
    ro.observe(canvas);
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [canvasRef]);

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const d = drag.current;
      const canvas = canvasRef.current;
      if (!d || !canvas) return;
      const b = canvas.getBoundingClientRect();
      const px = clamp((e.clientX - b.left) / b.width, 0, 1);
      const py = clamp((e.clientY - b.top) / b.height, 0, 1);
      onChange(computeRect(d.handle, d.start, px, py, d.startPx, d.startPy, aspect));
    };
    const onUp = () => {
      drag.current = null;
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [aspect, onChange, canvasRef]);

  if (!box) return null;

  const start = (handle: Handle) => (e: React.PointerEvent) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const b = canvas.getBoundingClientRect();
    drag.current = {
      handle,
      start: rect,
      startPx: clamp((e.clientX - b.left) / b.width, 0, 1),
      startPy: clamp((e.clientY - b.top) / b.height, 0, 1),
    };
  };

  const boxStyle: React.CSSProperties = {
    left: box.left + rect.x * box.width,
    top: box.top + rect.y * box.height,
    width: rect.width * box.width,
    height: rect.height * box.height,
  };

  const handles: Handle[] =
    aspect != null
      ? ['nw', 'ne', 'se', 'sw']
      : ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

  return (
    <div className="cropov" style={boxStyle}>
      <div className="cropov__grid" />
      <div className="cropov__move" onPointerDown={start('move')} />
      {handles.map((h) => (
        <div
          key={h}
          className={`cropov__h cropov__h--${h}`}
          onPointerDown={start(h)}
        />
      ))}
    </div>
  );
}
